import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import contractService from '../../services/contractService';
import ContractComplianceScanner from './components/ContractComplianceScanner';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';

const ContractManagement = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedContract, setSelectedContract] = useState(null);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await contractService?.getAll();
      setContracts(data);
    } catch (err) {
      setError(err?.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    // Filter changes handled by GlobalFilterBar component
  };

  const handleScanContract = (contract) => {
    setSelectedContract(contract);
    setShowScanner(true);
  };

  const handleScanComplete = (results) => {
    setShowScanner(false);
    setSelectedContract(null);
    loadContracts();
  };

  const handleCloseScanner = () => {
    setShowScanner(false);
    setSelectedContract(null);
  };

  const filteredContracts = contracts?.filter(contract => {
    if (selectedType === 'all') return true;
    return contract?.contractType === selectedType;
  }) || [];

  const stats = {
    total: contracts?.length || 0,
    active: contracts?.filter(c => c?.contractStatus === 'active')?.length || 0,
    fullTime: contracts?.filter(c => c?.contractType === 'full_time')?.length || 0,
    casual: contracts?.filter(c => c?.contractType === 'casual')?.length || 0,
    fixedTerm: contracts?.filter(c => c?.contractType === 'fixed_term')?.length || 0,
    nonCitizen: contracts?.filter(c => c?.contractType === 'non_citizen')?.length || 0
  };

  const contractTemplates = [
    {
      type: 'full_time',
      title: 'Full-Time Employment Contract',
      description: 'Standard employment contract with full benefits, leave entitlements, and superannuation',
      icon: 'Briefcase',
      clauses: ['Position and duties', 'Hours and overtime', 'Leave entitlements', 'Remuneration', 'Termination procedures']
    },
    {
      type: 'casual',
      title: 'Casual Employment Contract',
      description: 'Irregular hours contract with hourly rate and public holiday provisions',
      icon: 'Clock',
      clauses: ['Engagement basis', 'Hourly wage rate', 'Overtime basis', 'Public holiday work', 'Short notice periods']
    },
    {
      type: 'fixed_term',
      title: 'Fixed-Term Contract',
      description: 'Project-based or time-limited employment with defined end date',
      icon: 'Calendar',
      clauses: ['Term length', 'Project deliverables', 'Leave treatment', 'End-of-term procedures', 'Renewal conditions']
    },
    {
      type: 'non_citizen',
      title: 'Non-Citizen Employment Contract',
      description: 'Employment contract for non-citizens with work permit requirements and DLIR compliance',
      icon: 'Globe',
      clauses: ['Work permit details', 'Localization commitments', 'Permit contingencies', 'Training obligations', 'DLIR escalation contacts']
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading contracts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <DataRefreshIndicator />
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Contract Management</h1>
          <p className="text-muted-foreground">Generate and manage Employment Act 1978 compliant employment contracts</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error loading data</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="FileText" size={24} className="text-primary" />
              <span className="text-2xl font-bold text-foreground">{stats?.total}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Contracts</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="CheckCircle" size={24} className="text-success" />
              <span className="text-2xl font-bold text-foreground">{stats?.active}</span>
            </div>
            <p className="text-sm text-muted-foreground">Active Contracts</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Briefcase" size={24} className="text-accent" />
              <span className="text-2xl font-bold text-foreground">{stats?.fullTime}</span>
            </div>
            <p className="text-sm text-muted-foreground">Full-Time</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Globe" size={24} className="text-primary" />
              <span className="text-2xl font-bold text-foreground">{stats?.nonCitizen}</span>
            </div>
            <p className="text-sm text-muted-foreground">Non-Citizen</p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Contract Templates</h2>
          <p className="text-sm text-muted-foreground mb-6">Select a template to generate a compliant employment contract</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contractTemplates?.map((template) => (
              <div key={template?.type} className="border border-border rounded-lg p-6 hover:border-primary transition-colors">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-primary/10 rounded-lg p-3">
                    <Icon name={template?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">{template?.title}</h3>
                    <p className="text-sm text-muted-foreground">{template?.description}</p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Essential Clauses:</p>
                  <ul className="space-y-1">
                    {template?.clauses?.map((clause, idx) => (
                      <li key={idx} className="text-sm text-foreground flex items-center gap-2">
                        <Icon name="Check" size={14} className="text-success" />
                        {clause}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button variant="outline" size="sm" fullWidth iconName="Plus">
                  Generate Contract
                </Button>
              </div>
            ))}
          </div>
        </div>

        {showScanner && selectedContract && (
          <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Contract Compliance Scan</h2>
              <Button variant="ghost" size="sm" onClick={handleCloseScanner} iconName="X">
                Close
              </Button>
            </div>
            <ContractComplianceScanner 
              contract={selectedContract} 
              onScanComplete={handleScanComplete}
            />
          </div>
        )}

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Existing Contracts</h2>
            <div className="flex gap-2">
              <Button
                variant={selectedType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType('all')}
              >
                All
              </Button>
              <Button
                variant={selectedType === 'full_time' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType('full_time')}
              >
                Full-Time
              </Button>
              <Button
                variant={selectedType === 'casual' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType('casual')}
              >
                Casual
              </Button>
              <Button
                variant={selectedType === 'fixed_term' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType('fixed_term')}
              >
                Fixed-Term
              </Button>
              <Button
                variant={selectedType === 'non_citizen' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType('non_citizen')}
              >
                Non-Citizen
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contract Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Start Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Salary/Rate</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Version</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-muted-foreground">
                      No contracts found
                    </td>
                  </tr>
                ) : (
                  filteredContracts?.map((contract) => (
                    <tr key={contract?.id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {contract?.employee?.firstName} {contract?.employee?.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">{contract?.employee?.employeeNumber}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">{contract?.employee?.department}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                          {contract?.contractType?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                          contract?.contractStatus === 'active' ? 'bg-success/10 text-success' :
                          contract?.contractStatus === 'draft'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                        }`}>
                          {contract?.contractStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">
                        {new Date(contract?.startDate)?.toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">
                        {contract?.baseSalary ? `K${contract?.baseSalary?.toFixed(2)}` : 
                         contract?.hourlyRate ? `K${contract?.hourlyRate?.toFixed(2)}/hr` : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">v{contract?.versionNumber}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" iconName="Eye">
                            View
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            iconName="Shield"
                            onClick={() => handleScanContract(contract)}
                          >
                            Scan
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContractManagement;